// src/components/interfaces/InterfaceFormModal.tsx

import React from "react";
import type { Interface } from "../../types/interface";

export type FormType = "ethernet" | "vlan" | "ip" | null;

interface InterfaceFormModalProps {
  isOpen: boolean;
  activeForm: FormType;
  onClose: () => void;

  ethernetForm: any;
  setEthernetForm: React.Dispatch<React.SetStateAction<any>>;
  vlanForm: any;
  setVlanForm: React.Dispatch<React.SetStateAction<any>>;
  ipForm: any;
  setIpForm: React.Dispatch<React.SetStateAction<any>>;

  interfaces: Interface[];

  ethernetLoading: boolean;
  vlanLoading: boolean;
  ipLoading: boolean;

  handleEthernetSubmit: (e: React.FormEvent) => void;
  handleVlanSubmit: (e: React.FormEvent) => void;
  handleIPSubmit: (e: React.FormEvent) => void;
}

const InterfaceFormModal: React.FC<InterfaceFormModalProps> = ({
  isOpen,
  activeForm,
  onClose,
  ethernetForm,
  setEthernetForm,
  vlanForm,
  setVlanForm,
  ipForm,
  setIpForm,
  interfaces,
  ethernetLoading,
  vlanLoading,
  ipLoading,
  handleEthernetSubmit,
  handleVlanSubmit,
  handleIPSubmit,
}) => {
  if (!isOpen || !activeForm) return null;

  const renderTitle = () => {
    switch (activeForm) {
      case "ethernet":
        return "Add Ethernet Interface";
      case "vlan":
        return "Add VLAN Interface";
      case "ip":
        return "Attach IP Address";
      default:
        return "";
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-200">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {renderTitle()}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {activeForm === "ethernet" &&
                "Create a new Ethernet interface and configure its properties."}
              {activeForm === "vlan" &&
                "Create a VLAN interface on top of an existing interface."}
              {activeForm === "ip" &&
                "Attach an IP address to one of your interfaces."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        {/* Tab labels (visual only, control is via parent) */}
        <div className="mb-4 flex gap-2 rounded-xl bg-slate-50 p-1 text-xs font-medium text-slate-600">
          <button
            disabled={activeForm === "ethernet"}
            className={`flex-1 rounded-lg px-2 py-1 ${
              activeForm === "ethernet"
                ? "bg-white text-slate-900 shadow-sm"
                : "hover:bg-slate-100"
            }`}
          >
            Ethernet
          </button>
          <button
            disabled={activeForm === "vlan"}
            className={`flex-1 rounded-lg px-2 py-1 ${
              activeForm === "vlan"
                ? "bg-white text-slate-900 shadow-sm"
                : "hover:bg-slate-100"
            }`}
          >
            VLAN
          </button>
          <button
            disabled={activeForm === "ip"}
            className={`flex-1 rounded-lg px-2 py-1 ${
              activeForm === "ip"
                ? "bg-white text-slate-900 shadow-sm"
                : "hover:bg-slate-100"
            }`}
          >
            Attach IP
          </button>
        </div>

        {/* Body */}
        {activeForm === "ethernet" && (
          <form onSubmit={handleEthernetSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Name</label>
              <input
                type="text"
                placeholder="e.g. ether10-uplink"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ethernetForm.name}
                onChange={(e) =>
                  setEthernetForm((prev: any) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Comment
              </label>
              <input
                type="text"
                placeholder="Optional description"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ethernetForm.comment}
                onChange={(e) =>
                  setEthernetForm((prev: any) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">MTU</label>
                <input
                  type="number"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={ethernetForm.mtu}
                  onChange={(e) =>
                    setEthernetForm((prev: any) => ({
                      ...prev,
                      mtu: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  Speed (optional)
                </label>
                <input
                  type="text"
                  placeholder="auto / 1Gbps / 100Mbps"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={ethernetForm.speed}
                  onChange={(e) =>
                    setEthernetForm((prev: any) => ({
                      ...prev,
                      speed: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">ARP</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={ethernetForm.arp}
                  onChange={(e) =>
                    setEthernetForm((prev: any) => ({
                      ...prev,
                      arp: e.target.value,
                    }))
                  }
                >
                  <option value="enabled">enabled</option>
                  <option value="proxy-arp">proxy-arp</option>
                  <option value="reply-only">reply-only</option>
                  <option value="disabled">disabled</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  Auto Negotiation
                </label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={ethernetForm.auto_negotiation}
                  onChange={(e) =>
                    setEthernetForm((prev: any) => ({
                      ...prev,
                      auto_negotiation: e.target.value,
                    }))
                  }
                >
                  <option value="yes">yes</option>
                  <option value="no">no</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={ethernetLoading}
              className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {ethernetLoading ? "Adding…" : "Add Ethernet Interface"}
            </button>
          </form>
        )}

        {activeForm === "vlan" && (
          <form onSubmit={handleVlanSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                VLAN Name
              </label>
              <input
                type="text"
                placeholder="e.g. vlan10-office"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={vlanForm.name}
                onChange={(e) =>
                  setVlanForm((prev: any) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Base Interface
              </label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={vlanForm.interface}
                onChange={(e) =>
                  setVlanForm((prev: any) => ({
                    ...prev,
                    interface: e.target.value,
                  }))
                }
              >
                <option value="">Select interface</option>
                {interfaces.map((intf) => (
                  <option key={intf.name} value={intf.name}>
                    {intf.name} ({intf.type})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  VLAN ID
                </label>
                <input
                  type="number"
                  placeholder="e.g. 10"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={vlanForm.vlan_id}
                  onChange={(e) =>
                    setVlanForm((prev: any) => ({
                      ...prev,
                      vlan_id: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  MTU
                </label>
                <input
                  type="number"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                  value={vlanForm.mtu}
                  onChange={(e) =>
                    setVlanForm((prev: any) => ({
                      ...prev,
                      mtu: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Comment
              </label>
              <input
                type="text"
                placeholder="Optional description"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={vlanForm.comment}
                onChange={(e) =>
                  setVlanForm((prev: any) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
              />
            </div>

            <button
              type="submit"
              disabled={vlanLoading}
              className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {vlanLoading ? "Adding…" : "Add VLAN Interface"}
            </button>
          </form>
        )}

        {activeForm === "ip" && (
          <form onSubmit={handleIPSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                IP Address (CIDR)
              </label>
              <input
                type="text"
                placeholder="e.g. 192.168.10.1/24"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ipForm.address}
                onChange={(e) =>
                  setIpForm((prev: any) => ({ ...prev, address: e.target.value }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Interface
              </label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ipForm.interface}
                onChange={(e) =>
                  setIpForm((prev: any) => ({
                    ...prev,
                    interface: e.target.value,
                  }))
                }
              >
                <option value="">Select interface</option>
                {interfaces.map((intf) => (
                  <option key={intf.name} value={intf.name}>
                    {intf.name} ({intf.type})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Network (optional)
              </label>
              <input
                type="text"
                placeholder="Auto or 192.168.10.0"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ipForm.network}
                onChange={(e) =>
                  setIpForm((prev: any) => ({ ...prev, network: e.target.value }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">
                Comment (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. LAN gateway"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-slate-900/70"
                value={ipForm.comment}
                onChange={(e) =>
                  setIpForm((prev: any) => ({ ...prev, comment: e.target.value }))
                }
              />
            </div>

            <button
              type="submit"
              disabled={ipLoading}
              className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {ipLoading ? "Attaching…" : "Attach IP"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default InterfaceFormModal;
